/**
 * useJobPolling — Phase 2.A reusable polling hook.
 *
 * Polls GET /api/jobs/{jobId} every `intervalMs` (default 2.5 s) until
 * the job hits a terminal state (`completed`, `failed`, `cancelled`).
 * Returns `{ job, isPolling, error, stop }`. Caller passes `onTerminal`
 * to react to final state (toast, mutate parent state, etc.).
 *
 * Why a custom hook and not React Query?
 * - The job-monitor surface is the only polling consumer right now;
 *   a 60-line useEffect-based hook keeps deps minimal.
 * - The hook needs to stop cleanly when the parent unmounts AND when
 *   the job reaches a terminal state — easier to read in a tight
 *   useEffect than in a Query polling config.
 *
 * Usage:
 *   const { job, isPolling } = useJobPolling(jobId, {
 *     onTerminal: (j) => {
 *       if (j.status === "completed") toast.success(...);
 *     },
 *   });
 */
import { useEffect, useRef, useState } from "react";
import api from "./api";

const TERMINAL_STATES = new Set(["completed", "failed", "cancelled"]);
const DEFAULT_INTERVAL_MS = 2500;
const MAX_CONSECUTIVE_ERRORS = 5;

export function useJobPolling(jobId, opts = {}) {
  const {
    intervalMs = DEFAULT_INTERVAL_MS,
    onTerminal,
    enabled = true,
  } = opts;

  const [job, setJob] = useState(null);
  const [error, setError] = useState(null);
  const [isPolling, setIsPolling] = useState(false);
  const stopRef = useRef(false);
  const onTerminalRef = useRef(onTerminal);
  onTerminalRef.current = onTerminal;

  useEffect(() => {
    if (!jobId || !enabled) {
      setJob(null);
      setError(null);
      setIsPolling(false);
      return undefined;
    }

    stopRef.current = false;
    setIsPolling(true);
    setError(null);
    let timer = null;
    let consecutiveErrors = 0;

    const tick = async () => {
      if (stopRef.current) return;
      try {
        const { data } = await api.get(`/jobs/${jobId}`);
        consecutiveErrors = 0;
        setJob(data);
        if (data.status && TERMINAL_STATES.has(data.status)) {
          setIsPolling(false);
          if (onTerminalRef.current) {
            try { onTerminalRef.current(data); } catch { /* swallow */ }
          }
          return;
        }
      } catch (e) {
        consecutiveErrors += 1;
        if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
          setError(e?.response?.data?.detail || "Lost connection to job server");
          setIsPolling(false);
          return;
        }
      }
      timer = setTimeout(tick, intervalMs);
    };

    tick();

    return () => {
      stopRef.current = true;
      if (timer) clearTimeout(timer);
      setIsPolling(false);
    };
  }, [jobId, enabled, intervalMs]);

  const stop = () => {
    stopRef.current = true;
    setIsPolling(false);
  };

  return { job, error, isPolling, stop };
}
